import { useEffect, useRef } from 'react';

import { getEnvClassicAssetData } from 'helpers/assets';
import { getEnv } from 'helpers/env';

import { useProxyDirectoryStats } from 'hooks/useProxyProjection';

import type AccountService from 'services/account.service';
import { ModalService, StellarService } from 'services/globalServices';
import { StellarEvents } from 'services/stellar/events/events';

import DIceTrustlineModal from 'modals/DIceTrustlineModal';

const useDelegatedTrustlines = (account: AccountService | null) => {
    const owner = account?.accountId() ?? '';
    const projection = useProxyDirectoryStats(owner ? [owner] : []);
    const prompted = useRef({ identity: '', combinations: new Set<string>() });

    useEffect(() => {
        const identity = `${getEnv()}:${owner}`;
        if (prompted.current.identity !== identity) {
            prompted.current = { identity, combinations: new Set() };
        }
        if (!account) return undefined;
        const notify = (hasUpvote: boolean, hasGovern: boolean) => {
            const neededDIceTrustline =
                hasUpvote && account.getAssetBalance(getEnvClassicAssetData('dIce').asset) === null;
            const neededGDIceTrustline =
                hasGovern &&
                account.getAssetBalance(getEnvClassicAssetData('gdIce').asset) === null;
            const combination = `${neededDIceTrustline}:${neededGDIceTrustline}`;
            if (
                (neededDIceTrustline || neededGDIceTrustline) &&
                !prompted.current.combinations.has(combination)
            ) {
                prompted.current.combinations.add(combination);
                ModalService.openModal(DIceTrustlineModal, {
                    neededDIceTrustline,
                    neededGDIceTrustline,
                });
            }
        };
        if (projection.status === 'ready') {
            const incoming = projection.data.get(owner);
            if (incoming) {
                notify(incoming.upvoteIncomingStroops > 0n, incoming.governIncomingStroops > 0n);
            }
            return undefined;
        }
        if (projection.status !== 'disabled') return undefined;
        return StellarService.event.sub(({ type }) => {
            if (type !== StellarEvents.claimableUpdate) return;
            const delegators = StellarService.cb.getDelegatorLocks(owner);
            notify(
                delegators.some(
                    ({ asset }) => asset === getEnvClassicAssetData('upvoteIce').assetString,
                ),
                delegators.some(
                    ({ asset }) => asset === getEnvClassicAssetData('governIce').assetString,
                ),
            );
        });
    }, [account, owner, projection]);
};

export default useDelegatedTrustlines;
